
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { useCoachManagement } from "@/hooks/useCoachManagement";
import { BecomeCoachForm } from "@/components/coach/BecomeCoachForm";
import { SupervisionRequestsList } from "@/components/coach/SupervisionRequestsList";
import { SendSupervisionRequest } from "@/components/coach/SendSupervisionRequest";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { UserMinus } from "lucide-react";

const CoachDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('supervision');
  const [isDeleting, setIsDeleting] = useState(false);
  const { 
    isCoach, 
    coachProfile, 
    supervisionRequests, 
    activeTrainees,
    loading, 
    becomeCoach, 
    deleteCoachProfile,
    fetchSupervisionRequests 
  } = useCoachManagement();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (error) {
          console.error("Erreur d'authentification:", error);
          toast({
            variant: "destructive",
            title: t('dashboard.auth.errorTitle'),
            description: t('dashboard.auth.errorDescription'),
          });
          navigate("/auth");
          return;
        }

        if (!user) {
          console.log("Aucun utilisateur connecté, redirection vers auth");
          navigate("/auth");
          return;
        }

        console.log("Utilisateur authentifié:", user.id);
      } catch (error) {
        console.error("Erreur lors de la vérification d'authentification:", error);
        navigate("/auth");
      }
    };

    checkAuth();
  }, [navigate, toast]);

  const handleBecomeCoach = async (coachData: any) => {
    try {
      console.log("Tentative de création de coach avec les données:", coachData);
      await becomeCoach(coachData);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement coach:", error);
      // L'erreur est déjà gérée dans le hook
    }
  };

  const handleDeleteCoachProfile = async () => {
    setIsDeleting(true);
    try {
      await deleteCoachProfile();
      toast({
        title: t('dashboard.disconnect.success'),
        description: t('dashboard.disconnect.successDescription'),
      });
    } catch (error) {
      console.error("Erreur lors de la déconnexion coach:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
        <NavigationMenu />
        <div className="container mx-auto p-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-golf-600 mx-auto mb-4"></div>
              <p className="text-golf-700">{t('dashboard.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      
      <div className="container mx-auto p-4 md:p-8">
        <h1 className="text-3xl font-bold text-golf-800 mb-8">{t('dashboard.title')}</h1>
        
        {!isCoach ? (
          <div className="max-w-2xl mx-auto">
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>{t('dashboard.becomeCoach.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  {t('dashboard.becomeCoach.description')}
                </p>
              </CardContent>
            </Card>
            <BecomeCoachForm onSubmit={handleBecomeCoach} loading={loading} />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">{t('dashboard.tabs.overview')}</TabsTrigger>
              <TabsTrigger value="requests">{t('dashboard.tabs.requests')}</TabsTrigger>
              <TabsTrigger value="send">{t('dashboard.tabs.send')}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('dashboard.profile.title')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {coachProfile ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <p><strong>{t('dashboard.profile.name')}:</strong> {coachProfile.coach_name}</p>
                          <p><strong>{t('dashboard.profile.email')}:</strong> {coachProfile.contact_email}</p>
                          {coachProfile.certification && (
                            <p><strong>{t('dashboard.profile.certification')}:</strong> {coachProfile.certification}</p>
                          )}
                          {coachProfile.specialization && (
                            <p><strong>{t('dashboard.profile.specialization')}:</strong> {coachProfile.specialization}</p>
                          )}
                        </div>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm" className="mt-4">
                              <UserMinus className="w-4 h-4 mr-2" />
                              {t('dashboard.disconnect.button')}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t('dashboard.disconnect.title')}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('dashboard.disconnect.description')}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('dashboard.disconnect.cancel')}</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={handleDeleteCoachProfile}
                                disabled={isDeleting}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {isDeleting ? t('dashboard.disconnect.deleting') : t('dashboard.disconnect.confirm')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ) : (
                      <p className="text-gray-500">{t('dashboard.profile.loadingProfile')}</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('dashboard.statistics.title')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p>{t('dashboard.statistics.pendingRequests')}: {supervisionRequests.filter(r => r.status === 'pending').length}</p>
                      <p>{t('dashboard.statistics.activeSupervisions')}: {supervisionRequests.filter(r => r.status === 'accepted').length}</p>
                      <p>{t('dashboard.statistics.supervisedTrainees')}: {activeTrainees.length}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="requests">
              <div>
                <h2 className="text-xl font-semibold mb-4">{t('dashboard.requestsSection.title')}</h2>
                <SupervisionRequestsList 
                  requests={supervisionRequests} 
                  onUpdate={fetchSupervisionRequests}
                />
              </div>
            </TabsContent>

            <TabsContent value="send">
              <div>
                <h2 className="text-xl font-semibold mb-4">{t('dashboard.sendSection.title')}</h2>
                <SendSupervisionRequest />
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default CoachDashboard;
